import { Injectable } from '@angular/core';
import { LogRecord, SeverityNumber } from '@opentelemetry/api-logs';
import { ReplaySubject } from 'rxjs';

import { LogUtil } from './internal/log-util';
import { AdditionalLogproperties } from './logger.types';

const REPLAY_LOGS_TIME = 30000;
const REPLAY_LOGS_COUNT = Number.POSITIVE_INFINITY;

/**
 *  Can be customized (onprem only) with {@link configureLoggerService}
 *  @example
 *  export function provideCore(
 *     options: CoreOptions,
 * ): Array<Provider | EnvironmentProviders> {
 *     return [
 *     // ...
 *          ...configureLoggerService({
 *                consoleMonitorLevel: 'error',
 *                loggingEndpoint: '/api/log';
 *          })
 *     ]
 * }
 */
@Injectable({
    providedIn: 'root'
})
export class Logger {
    readonly #DEFAULT_LOGGER_NAME = 'SPA Logger';
    #logsSubject$ = new ReplaySubject<Required<LogRecord>>(REPLAY_LOGS_COUNT, REPLAY_LOGS_TIME);
    logs$ = this.#logsSubject$.asObservable();
    #loggerName = this.#DEFAULT_LOGGER_NAME;

    #log(severityNumber: SeverityNumber, message: string, additionalLogproperties: AdditionalLogproperties): void {
        if (message.length > 0) {
            const log = LogUtil.createLogRecord(severityNumber, message, this.#loggerName, additionalLogproperties);
            this.#logsSubject$.next(log);
        }
    }

    debug(message: string, additionalLogproperties: AdditionalLogproperties = {}): void {
        this.#log(SeverityNumber.DEBUG, message, additionalLogproperties);
    }

    warn(message: string, additionalLogproperties: AdditionalLogproperties = {}): void {
        this.#log(SeverityNumber.WARN, message, additionalLogproperties);
    }

    info(message: string, additionalLogproperties: AdditionalLogproperties = {}): void {
        this.#log(SeverityNumber.INFO, message, additionalLogproperties);
    }

    trace(message: string, additionalLogproperties: AdditionalLogproperties = {}): void {
        this.#log(SeverityNumber.TRACE, message, additionalLogproperties);
    }

    error(messageOrError: string | Error, additionalLogproperties: AdditionalLogproperties = {}): void {
        if (messageOrError instanceof Error) {
            additionalLogproperties.exception = messageOrError;
            this.#log(SeverityNumber.ERROR, messageOrError.message, additionalLogproperties);
        } else {
            if (!additionalLogproperties.exception) {
                additionalLogproperties.exception = new Error();
            }
            this.#log(SeverityNumber.ERROR, messageOrError, additionalLogproperties);
        }
    }

    createCustomLogger(loggerName: string): Logger {
        const customLogger = new Logger();
        customLogger.#loggerName = loggerName;
        customLogger.#logsSubject$ = this.#logsSubject$;
        customLogger.logs$ = this.logs$;
        return customLogger;
    }
}
